import React, { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Gift, Sparkles, Banknote, ShieldCheck, Copy, Check } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

const presetAmounts = [25, 50, 100, 250];

export default function Donate() {
    const { t, lang } = useLang();
    const d = t.donatePage;

    const [amount, setAmount] = useState(50);
    const [custom, setCustom] = useState("");
    const [frequency, setFrequency] = useState("once");
    const [type, setType] = useState("regular");
    const [area, setArea] = useState("general");
    const [form, setForm] = useState({
        donor_name: "",
        donor_email: "",
        recipient_name: "",
        recipient_email: "",
        gift_message: "",
    });
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const finalAmount = custom ? parseFloat(custom) : amount;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!finalAmount || finalAmount <= 0) {
            toast.error(d.error);
            return;
        }
        setLoading(true);
        try {
            await api.submitDonation({
                donor_name: form.donor_name,
                donor_email: form.donor_email,
                amount: finalAmount,
                currency: "EUR",
                frequency,
                donation_type: type,
                recipient_name: type === "gift" ? form.recipient_name : "",
                recipient_email: type === "gift" ? form.recipient_email : "",
                gift_message: type === "gift" ? form.gift_message : "",
                dedicated_area: area,
                language: lang,
            });
            toast.success(d.success);
            setForm({ donor_name: "", donor_email: "", recipient_name: "", recipient_email: "", gift_message: "" });
            setCustom("");
            setAmount(50);
        } catch (err) {
            toast.error(d.error);
        } finally {
            setLoading(false);
        }
    };

    const copyIban = () => {
        navigator.clipboard.writeText(d.iban);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };

    return (
        <div data-testid="page-donate" className="px-6 md:px-10 py-12 md:py-20 relative overflow-hidden">
            <div aria-hidden className="absolute -top-20 right-0 w-[480px] h-[480px] rounded-full bg-caireen-yellow/20 blur-3xl pointer-events-none" />
            <div aria-hidden className="absolute top-1/2 -left-32 w-[420px] h-[420px] rounded-full bg-caireen-orange/15 blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl"
                >
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-caireen-red/10 text-caireen-red font-sans text-xs tracking-widest uppercase">
                        <Heart className="w-3.5 h-3.5" fill="currentColor" />
                        {d.eyebrow}
                    </span>
                    <h1 className="mt-5 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05] text-balance">
                        {d.title}
                    </h1>
                    <p className="mt-5 font-sans text-lg text-caireen-ink/70 max-w-2xl">{d.sub}</p>
                </motion.div>

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Form */}
                    <motion.form
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        onSubmit={onSubmit}
                        className="lg:col-span-7 bg-white rounded-3xl ring-1 ring-caireen-ink/10 p-7 md:p-10 space-y-7"
                        data-testid="donate-form"
                    >
                        {/* Amount */}
                        <div className="space-y-3">
                            <Label className="font-sans text-sm uppercase tracking-widest text-caireen-ink/60">{d.amountTitle}</Label>
                            <div className="grid grid-cols-4 gap-2">
                                {presetAmounts.map((a) => (
                                    <button
                                        type="button"
                                        key={a}
                                        data-testid={`donate-amount-${a}`}
                                        onClick={() => { setAmount(a); setCustom(""); }}
                                        className={`py-4 rounded-2xl font-serif text-2xl transition-all ${
                                            amount === a && !custom
                                                ? "bg-caireen-orange text-white shadow-[0_10px_24px_-12px_rgba(232,133,51,0.6)]"
                                                : "bg-caireen-cream ring-1 ring-caireen-ink/10 text-caireen-ink hover:ring-caireen-orange/50"
                                        }`}
                                    >
                                        €{a}
                                    </button>
                                ))}
                            </div>
                            <Input
                                type="number"
                                min="1"
                                step="1"
                                placeholder={d.customAmount}
                                value={custom}
                                data-testid="donate-custom-amount"
                                onChange={(e) => setCustom(e.target.value)}
                                className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12 font-sans"
                            />
                        </div>

                        {/* Frequency */}
                        <div className="space-y-3">
                            <Label className="font-sans text-sm uppercase tracking-widest text-caireen-ink/60">Frequency</Label>
                            <div className="grid grid-cols-3 gap-2">
                                {["once", "monthly", "yearly"].map((f) => (
                                    <button
                                        type="button"
                                        key={f}
                                        data-testid={`donate-freq-${f}`}
                                        onClick={() => setFrequency(f)}
                                        className={`py-3 rounded-full font-sans text-sm font-semibold transition-all ${
                                            frequency === f
                                                ? "bg-caireen-ink text-caireen-cream"
                                                : "bg-caireen-cream ring-1 ring-caireen-ink/10 text-caireen-ink/70"
                                        }`}
                                    >
                                        {d.frequency[f]}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Type */}
                        <div className="space-y-3">
                            <Label className="font-sans text-sm uppercase tracking-widest text-caireen-ink/60">Type</Label>
                            <RadioGroup value={type} onValueChange={setType} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label data-testid="donate-type-regular" className={`cursor-pointer p-4 rounded-2xl border-2 transition-all flex items-start gap-3 ${type === "regular" ? "border-caireen-orange bg-caireen-orange/5" : "border-caireen-ink/10 hover:border-caireen-ink/30"}`}>
                                    <RadioGroupItem value="regular" className="mt-1" />
                                    <div>
                                        <p className="font-sans font-semibold flex items-center gap-1.5"><Heart className="w-4 h-4 text-caireen-orange" />{d.typeRegular}</p>
                                    </div>
                                </label>
                                <label data-testid="donate-type-gift" className={`cursor-pointer p-4 rounded-2xl border-2 transition-all flex items-start gap-3 ${type === "gift" ? "border-caireen-orange bg-caireen-orange/5" : "border-caireen-ink/10 hover:border-caireen-ink/30"}`}>
                                    <RadioGroupItem value="gift" className="mt-1" />
                                    <div>
                                        <p className="font-sans font-semibold flex items-center gap-1.5"><Gift className="w-4 h-4 text-caireen-red" />{d.typeGift}</p>
                                    </div>
                                </label>
                            </RadioGroup>
                        </div>

                        {/* Donor */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="font-sans text-sm">{d.donorName}</Label>
                                <Input data-testid="donor-name" required value={form.donor_name} onChange={onChange("donor_name")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12" />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-sans text-sm">{d.donorEmail}</Label>
                                <Input data-testid="donor-email" required type="email" value={form.donor_email} onChange={onChange("donor_email")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12" />
                            </div>
                        </div>

                        {/* Gift fields */}
                        {type === "gift" && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                className="space-y-4 rounded-2xl bg-caireen-yellow/10 p-5 ring-1 ring-caireen-yellow/40"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="font-sans text-sm">{d.recipientName}</Label>
                                        <Input data-testid="recipient-name" value={form.recipient_name} onChange={onChange("recipient_name")} className="rounded-xl bg-white border-caireen-ink/15 h-12" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="font-sans text-sm">{d.recipientEmail}</Label>
                                        <Input data-testid="recipient-email" type="email" value={form.recipient_email} onChange={onChange("recipient_email")} className="rounded-xl bg-white border-caireen-ink/15 h-12" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="font-sans text-sm">{d.giftMessage}</Label>
                                    <Textarea data-testid="gift-message" rows={3} value={form.gift_message} onChange={onChange("gift_message")} className="rounded-xl bg-white border-caireen-ink/15" />
                                </div>
                            </motion.div>
                        )}

                        {/* Area */}
                        <div className="space-y-2">
                            <Label className="font-sans text-sm">{d.area}</Label>
                            <Select value={area} onValueChange={setArea}>
                                <SelectTrigger data-testid="donate-area" className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(d.areas).map(([k, v]) => (
                                        <SelectItem key={k} value={k}>{v}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="donate-submit"
                            className="w-full rounded-full bg-caireen-red hover:bg-caireen-orange text-white font-bold py-7 text-base shadow-[0_18px_36px_-12px_rgba(217,53,44,0.55)]"
                        >
                            <Heart className="w-5 h-5 mr-2" fill="currentColor" />
                            {d.submit} · €{finalAmount || 0}
                        </Button>

                        <p className="font-sans text-xs text-caireen-ink/55 inline-flex items-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            100% directly to projects · ZVR 1121813363
                        </p>
                    </motion.form>

                    {/* Side panel */}
                    <motion.aside
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="lg:col-span-5 space-y-6"
                    >
                        <div className="rounded-3xl overflow-hidden ring-1 ring-caireen-ink/10 aspect-[4/3] relative">
                            <img src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&w=900&q=80" alt="Community" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-caireen-ink/60 via-caireen-ink/0 to-transparent" />
                            <div className="absolute bottom-5 left-5 right-5 text-caireen-cream">
                                <p className="font-serif italic text-2xl leading-tight">{'"Together we build bridges."'}</p>
                            </div>
                        </div>

                        <div className="rounded-3xl bg-caireen-ink text-caireen-cream p-7" data-testid="bank-details">
                            <div className="flex items-center gap-2 font-sans text-xs uppercase tracking-widest text-caireen-yellow">
                                <Banknote className="w-4 h-4" />
                                {d.bankTitle}
                            </div>
                            <p className="mt-3 font-sans text-sm text-caireen-cream/80">{d.bankSub}</p>
                            <div className="mt-5 space-y-3">
                                <div>
                                    <p className="font-sans text-[11px] uppercase tracking-widest text-caireen-cream/55">Holder</p>
                                    <p className="font-serif text-lg">{d.holder}</p>
                                </div>
                                <div>
                                    <p className="font-sans text-[11px] uppercase tracking-widest text-caireen-cream/55">IBAN</p>
                                    <button onClick={copyIban} data-testid="copy-iban" className="mt-1 inline-flex items-center gap-2 font-mono text-base bg-white/5 px-3 py-2 rounded-xl border border-white/15 hover:bg-white/10 transition-colors">
                                        {d.iban}
                                        {copied ? <Check className="w-4 h-4 text-caireen-yellow" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-3xl bg-caireen-yellow/15 p-6 ring-1 ring-caireen-yellow/40">
                            <Sparkles className="w-5 h-5 text-caireen-orange" />
                            <p className="mt-3 font-serif text-xl leading-tight text-caireen-ink">
                                Every contribution becomes an opportunity for someone, somewhere.
                            </p>
                        </div>
                    </motion.aside>
                </div>
            </div>
        </div>
    );
}
